import { useMemo } from "react";
import Loading from "_shared/components/Loading/Loading";
import LineChart from "_shared/components/TradingView/LineChart";
import { useChartDataQuery } from "_shared/graphql";
import styles from "../Chart.module.scss";
import Legend from "../Legend/Legend";
import { massageData } from "../utilities";

interface Props {
  ticker: string;
}

const formatDate = (date: Date) => date.toLocaleDateString("fr-CA");

function MomentumIndicator({ ticker }: Props) {
  const lastYear = new Date(Date.now() - 356 * 24 * 60 * 60 * 1000);
  const variables = {
    ticker,
    from: formatDate(lastYear),
    to: formatDate(new Date()),
    interval: "daily",
    fields: ["timestamp", "px_last", "30D_ST_PRICE", "90D_MT_PRICE"],
  };
  const { data, loading } = useChartDataQuery({ variables });

  const chartData = useMemo(() => {
    if (!data?.get_charting_data?.data) return null;
    const price = massageData(data.get_charting_data.data, "px_last");
    const shortTerm = massageData(data.get_charting_data.data, "30D_ST_PRICE");
    const mediumTerm = massageData(data.get_charting_data.data, "90D_MT_PRICE");
    return [
      {
        name: "Price",
        data: price,
        color: "#fff",
      },
      {
        name: "Short term",
        data: shortTerm,
        color: "#D3D335",
      },
      {
        name: "Medium term",
        data: mediumTerm,
        color: "#3656D4",
      },
    ];
  }, [data]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Momentum indicator</h3>
        <div style={{ position: 'absolute', marginBottom: -25 }}>
          <Legend labels={[{ name: 'Bearish', color: 'red' }, { name: 'Bullish', color: 'green' }]} />
        </div>
        <Legend
          labels={
            chartData?.map((series) => ({
              name: series.name,
              color: series.color,
            })) || []
          }
        />
      </div>

      <div className={styles.graphContainer}>
        {chartData ? (
          <LineChart data={chartData} curvedLines ticker={ticker} title="Momentum Indicator" />
        ) : (
          <div className={styles.graphState}>
            {loading ? <Loading variant="inner" /> : "Error loading data"}
          </div>
        )}
      </div>
    </div>
  );
}

export default MomentumIndicator;
